"use client";

// biome-ignore lint/nursery/noDeprecatedImports: upstream Contentful hook is still required for live preview updates
import { useContentfulLiveUpdates } from "@scott/contentful/live-preview-init";
import type { ReactNode } from "react";
import { ContentBlocks } from "@/components/blocks/content-blocks/content-blocks";
import { useServerBlockRefresh } from "@/hooks/use-server-block-refresh";
import { contentBlocksAdapter } from "@/services/contentful/assemblies/page/adapter/content-blocks.adapter";
import type { ContentPage } from "@/services/contentful/assemblies/page/fetch-page-by-path";
import { withSectionDocuments } from "@/services/contentful/assemblies/sections/section-documents";
import { PageByPathDocument } from "@/services/contentful/generated/contentful-sdk.generated";
import type { PageContext } from "@/types/page-context";
import { ContentPageSurface } from "./content-page-surface";

const LIVE_PREVIEW_QUERY = withSectionDocuments(PageByPathDocument);

type ContentPageLivePreviewProps = {
  /** The active market's `/{country}/{lang}`, for internal page links. */
  basePath: string;
  locale: string;
  page: ContentPage;
  pageContext: PageContext["pageContext"];
  serverSlots: Map<string, ReactNode>;
};

export function ContentPageLivePreview(
  props: Readonly<ContentPageLivePreviewProps>
) {
  const updatedPage = useContentfulLiveUpdates(props.page, {
    locale: props.locale,
    query: LIVE_PREVIEW_QUERY,
  });

  useServerBlockRefresh(
    updatedPage,
    (page) => page.sectionsCollection?.items ?? [],
    props.serverSlots
  );

  const pageBlocks = contentBlocksAdapter(
    [...(updatedPage.sectionsCollection?.items ?? [])],
    props.locale,
    props.basePath
  );

  return (
    <ContentPageSurface firstBlock={pageBlocks[0]}>
      <ContentBlocks
        blocks={pageBlocks}
        pageContext={props.pageContext}
        serverSlots={props.serverSlots}
      />
    </ContentPageSurface>
  );
}
